import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, Sparkles, Shield, Leaf, FlaskConical, UserCog, Scan,
  PawPrint, UserCircle, Heart, Fish, ChefHat, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from '../components/AuthModal';
import { HeroRotator } from '../components/home/HeroRotator';

export const HomePage = ({ onNavigate, tedAiEnabled }) => {
  const { customer } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const navigate = useNavigate();

  const tools = [
    {
      id: 'sample-box',
      icon: Package,
      title: 'Build Your Own Custom Sample Box',
      description: 'Choose from our complete range of recipes and create a personalised sample box tailored to your dog\'s tastes and needs. Try different proteins and flavours before committing to a full bag.',
      cta: 'Start Building',
      color: 'from-[#0FA49C] to-[#0d8f88]',
      iconBg: 'bg-[#0FA49C]',
      image: 'https://images.unsplash.com/photo-1565674244283-993fb27a215f?w=400&q=80',
    },
    {
      id: 'ted-ai',
      icon: Scan,
      title: 'Scan Your Current Dog Food',
      description: 'Use Ted AI to scan your current dog food bag and receive matched recommendations from our complete range. Add those matches to a sample box, or upgrade to explore even more options.',
      cta: 'Scan Now',
      color: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-500',
      hidden: !tedAiEnabled,
      image: 'https://images.unsplash.com/photo-1692906456159-f93a6f3df407?w=400&q=80',
    },
    {
      id: 'get-matched',
      icon: PawPrint,
      title: 'Get Your Dog Matched',
      description: 'Tell Ted AI about your dog\'s breed, age, weight and sensitivities, and we\'ll recommend the most suitable foods from our range, with samples and trial bag options to help you test before switching.',
      cta: 'Get Matched',
      color: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-500',
      requiresAuth: true,
      image: 'https://images.unsplash.com/photo-1721656363841-93e97a879979?w=400&q=80',
    },
    {
      id: 'account',
      icon: UserCircle,
      title: 'Access Your Ted AI Account',
      description: 'Track current food prices, update your dog\'s profile, save recommendations, and unlock exclusive offers designed to help you feed smarter over time.',
      cta: customer ? 'My Account' : 'Sign In',
      color: 'from-amber-500 to-orange-500',
      iconBg: 'bg-amber-500',
      image: 'https://images.unsplash.com/photo-1589193008497-5b7e12ed050b?w=400&q=80',
    },
  ];

  const handleToolClick = (tool) => {
    if (tool.comingSoon) return;
    if (tool.id === 'account') {
      if (customer) {
        onNavigate('account');
      } else {
        setShowAuth(true);
      }
    } else if (tool.requiresAuth && !customer) {
      setShowAuth(true);
    } else {
      onNavigate(tool.id);
    }
  };

  const heroBenefits = [
    { icon: Leaf, label: 'Grain Free Recipes' },
    { icon: ChefHat, label: '11 Delicious Flavours' },
    { icon: PawPrint, label: 'Every Life Stage' },
    { icon: FlaskConical, label: 'Hydrolysed Options' },
    { icon: Fish, label: 'Fresh Meat or Fish #1' },
  ];

  const trustBadges = [
    { icon: Leaf, label: 'Made With Care' },
    { icon: Heart, label: 'Loved By Dogs' },
    { icon: Shield, label: 'Trusted By Owners' },
    { icon: Sparkles, label: 'Better Nutrition' },
  ];

  return (
    <div className="min-h-[80vh]" data-testid="homepage">
      {/* Hero — rustic teal hero banner */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundColor: '#1f6d62',
          backgroundImage: `
            radial-gradient(circle at 20% 15%, rgba(160, 218, 57, 0.08) 0%, transparent 45%),
            radial-gradient(circle at 85% 80%, rgba(160, 218, 57, 0.10) 0%, transparent 50%),
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")
          `,
        }}
        data-testid="home-hero"
      >
        {/* Admin icon */}
        <button
          onClick={() => navigate('/admin/login')}
          className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm hover:bg-[#a0da39] hover:text-[#1f6d62] text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-sm z-20 border border-white/20"
          title="Admin Login"
          data-testid="admin-login-icon"
        >
          <UserCog className="w-5 h-5" />
        </button>

        {/* Decorative brush strokes */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
          <svg className="absolute -top-8 -left-12 w-64 h-24 opacity-20" viewBox="0 0 300 80" fill="none">
            <path d="M0 40 Q 75 10, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <svg className="absolute bottom-6 right-8 w-72 h-20 opacity-15" viewBox="0 0 300 80" fill="none">
            <path d="M0 40 Q 75 70, 150 40 T 300 40" stroke="#a0da39" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8 lg:pt-10 lg:pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
            {/* Left — copy + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              {!customer ? (
                <>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-3">
                    <Sparkles className="w-3 h-3" /> Free — 30 second sign up
                  </span>
                  <h1 className="text-white font-extrabold uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl">
                    Meet Ted —<br/>
                    <span className="text-[#a0da39]">Your Dog&apos;s Personal AI Assistant</span>
                  </h1>
                  <p
                    className="text-[#a0da39] mt-3 text-xl sm:text-2xl italic"
                    style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
                  >
                    Smarter food. Faster ordering. Happier dogs.
                  </p>

                  <p className="text-white/85 mt-3 max-w-xl text-sm leading-relaxed">
                    One free account gives Ted everything he needs to recommend the best food, remember your dog&apos;s favourites, unlock exclusive offers and make ordering faster.
                  </p>

                  {/* CTAs — signup led */}
                  <div className="mt-5 flex flex-col sm:flex-row gap-2.5 sm:items-center">
                    <button
                      onClick={() => onNavigate('signup')}
                      className="group inline-flex items-center justify-center gap-2 bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-extrabold uppercase tracking-wide px-5 py-3 rounded-full shadow-lg shadow-[#a0da39]/20 transition-all duration-200 hover:-translate-y-0.5 text-sm"
                      data-testid="hero-cta-signup"
                    >
                      Create Free Account
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                    <button
                      onClick={() => onNavigate('sample-box')}
                      className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-3 rounded-full border border-white/25 backdrop-blur-sm transition-all duration-200 text-sm"
                      data-testid="hero-cta-sample-box"
                    >
                      <Package className="w-4 h-4" />
                      Browse Sample Boxes
                    </button>
                  </div>

                  {/* Micro-benefits — 3 emoji chips right under CTAs */}
                  <div className="mt-5 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                      🐶 Personalised recommendations
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                      💰 Price drop alerts
                    </span>
                    <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full backdrop-blur-sm">
                      🎁 Member-only rewards
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <h1 className="text-white font-extrabold uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl">
                    Welcome back,<br/>
                    <span className="text-[#a0da39]">{(customer.name || '').split(' ')[0] || 'friend'}</span>
                  </h1>
                  <p
                    className="text-[#a0da39] mt-3 text-xl sm:text-2xl italic"
                    style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
                  >
                    Let&apos;s find your dog&apos;s next favourite.
                  </p>
                  <p className="text-white/85 mt-3 max-w-xl text-sm leading-relaxed">
                    Scan a bag, build a sample box, or get Ted to match a new food to your dog&apos;s profile.
                  </p>
                  <div className="mt-5 flex flex-col sm:flex-row gap-2.5 sm:items-center">
                    <button
                      onClick={() => onNavigate('sample-box')}
                      className="group inline-flex items-center justify-center gap-2 bg-[#a0da39] hover:bg-[#b4e856] text-[#1f3d2f] font-extrabold uppercase tracking-wide px-5 py-3 rounded-full shadow-lg shadow-[#a0da39]/20 transition-all duration-200 hover:-translate-y-0.5 text-sm"
                      data-testid="hero-cta-sample-box"
                    >
                      Build Your Sample Box
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                    {tedAiEnabled && (
                      <button
                        onClick={() => onNavigate('ted-ai')}
                        className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-3 rounded-full border border-white/25 backdrop-blur-sm transition-all duration-200 text-sm"
                        data-testid="hero-cta-scan"
                      >
                        <Scan className="w-4 h-4" />
                        Scan Your Current Bag
                      </button>
                    )}
                    <button
                      onClick={() => onNavigate('account')}
                      className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-5 py-3 rounded-full border border-white/25 backdrop-blur-sm transition-all duration-200 text-sm"
                      data-testid="hero-cta-account"
                    >
                      <UserCircle className="w-4 h-4" />
                      My Account
                    </button>
                  </div>
                </>
              )}

              {/* Benefit icons — always shown (grain-free / life-stage badges) */}
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3">
                {heroBenefits.map((b) => (
                  <div
                    key={b.label}
                    className="flex items-center gap-2"
                    data-testid={`hero-benefit-${b.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#a0da39]/15 border border-[#a0da39]/40 flex items-center justify-center flex-shrink-0">
                      <b.icon className="w-4 h-4 text-[#a0da39]" />
                    </div>
                    <span className="text-white text-[11px] sm:text-xs font-semibold uppercase tracking-wide leading-tight max-w-[110px]">
                      {b.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right — Rotating tool card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5 relative"
            >
              <HeroRotator
                tools={tools.filter(t => !t.hidden)}
                onSelect={handleToolClick}
                customer={customer}
              />
            </motion.div>
          </div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 lg:mt-8 border-t border-white/15 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            {trustBadges.map((t) => (
              <div key={t.label} className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#a0da39]/20 border border-[#a0da39]/40 flex items-center justify-center flex-shrink-0">
                  <t.icon className="w-3.5 h-3.5 text-[#a0da39]" />
                </div>
                <span className="text-white text-[10px] sm:text-xs font-bold uppercase tracking-wide leading-tight">
                  {t.label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Intro strip */}
      <div className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-2 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#222] tracking-tight mb-3 leading-tight">
            Smarter Ways to Find the <span className="text-[#0FA49C]">Right Food</span> for Your Dog
          </h2>
          <p className="text-[#666] text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Finding the right food shouldn't feel complicated. <strong className="text-[#0FA49C]">Ted AI</strong> gives you four smarter ways to find better nutrition — without wasting money on bags your dog won't eat.
          </p>
        </div>
      </div>

      {/* Tool Cards */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {tools.filter(t => !t.hidden).map((tool, i) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              onClick={() => handleToolClick(tool)}
              className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-200 group ${
                tool.comingSoon
                  ? 'border-gray-200 opacity-75 cursor-default'
                  : 'border-gray-200 hover:border-[#0FA49C] hover:shadow-lg cursor-pointer'
              }`}
              data-testid={`tool-card-${tool.id}`}
            >
              {tool.comingSoon && (
                <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
                  Coming Soon
                </span>
              )}

              {tool.image && (
                <div className="h-36 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-2xl">
                  <img src={tool.image} alt="" className="w-full h-full object-cover" />
                </div>
              )}

              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl ${tool.iconBg} flex items-center justify-center flex-shrink-0`}>
                  <tool.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-lg text-[#222] leading-snug">{tool.title}</h3>
              </div>
              <p className="text-sm text-[#666] leading-relaxed mb-4">{tool.description}</p>

              {!tool.comingSoon && (
                <span className={`inline-flex items-center gap-1.5 text-sm font-bold text-[#0FA49C] group-hover:underline`}>
                  {tool.cta}
                  <svg className="w-4 h-4 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </span>
              )}
            </motion.div>
          ))}
        </div>

        {/* Bottom tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center text-sm text-[#999] mt-8 max-w-lg mx-auto"
        >
          Whether you want to compare, try, match or save — Ted AI gives you a smarter way to find the right food for your dog.
        </motion.p>
      </div>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
};
